import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mail, MapPin, ChevronDown, Copy } from "lucide-react";
import { useAppConfigContext } from "../context/AppConfigContext";

function Contact() {
  const { config } = useAppConfigContext();
  const countryCodes = [
    { code: "+234", label: "Nigeria", flag: "🇳🇬" },
    { code: "+1", label: "United States", flag: "🇺🇸" },
    { code: "+44", label: "United Kingdom", flag: "🇬🇧" },
    { code: "+91", label: "India", flag: "🇮🇳" },
    { code: "+61", label: "Australia", flag: "🇦🇺" },
  ];

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const filteredCountryCodes = countryQuery.trim()
    ? countryCodes.filter(
        (country) =>
          country.code.toLowerCase().includes(countryQuery.trim().toLowerCase()) ||
          country.label.toLowerCase().includes(countryQuery.trim().toLowerCase())
      )
    : countryCodes;

  const supportEmail = config.supportEmail;
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(supportEmail);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      <main className="space-y-16">
        <section className="max-w-[80%] mx-auto px-4 text-center ">
          <div className="space-y-4">
          <h1 className="text-2xl font-semibold text-[#61013B]">Contact us</h1>
          <p className="text- text-gray-700 font-medium max-w-3xl mx-auto ">
            Questions, feedback, or partnership ideas? We’d love to hear from you.
            Whether you’re a traveler, service provider, or just exploring what
            PULO has to offer — we’re here to help.
          </p>
          </div>


          <div className="font-medium grid grid-cols-1 sm:grid-cols-2 gap-4 mt-16">
            <div className="flex flex-col items-center gap-2">
              <div className="w-11 h-11 rounded-xl bg-[#f7ecf4] flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#570033]" />
              </div>
              <p className="text-sm text-gray-800">Lagos, Nigeria</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-11 h-11 rounded-xl bg-[#f7ecf4] flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#570033]" />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="text-sm text-gray-800 inline-flex items-center gap-1 hover:text-[#570033]"
                >
                  {supportEmail}
                  <Copy className="w-4 h-4" />
                </button>
                {copiedEmail && (
                  <span className="text-xs font-semibold text-[#570033]">
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-[80%] mx-auto px-4 mt-10 grid md:grid-cols-2 gap-6 items-center">
          <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <img
              src="/images/support.jpg"
              alt="Support"
              className="w-full h-full object-cover"
            />
          </div>

          {/* right column – form pushed to far right */}
          <form className="space-y-6 w-full sm:max-w-sm ml-auto">
            <div className="grid grid-cols-1 gap-6">
              <div className="text-left">
                <label className="block text-xs  text-gray-800 mb-1">
                  Enter Full Name
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  className="
                    w-full border border-gray-300 font-medium opacity-80 rounded px-3 py-2 text-sm outline-none
                    placeholder:text-black placeholder:font-medium
                  "
                />
              </div>

              <div className="text-left">
                <label className="block text-xs  text-gray-800 mb-1">
                  Enter Email
                </label>
                <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="w-full text-sm outline-none font-medium opacity-80 placeholder:text-black placeholder:font-medium"
                  />
                </div>
              </div>

              <div className="text-left">
                <label className="block text-xs text-gray-800 mb-1">
                  Phone No
                </label>
                <div className="relative">
                  <div className="flex items-center gap-2 border border-gray-300 rounded-xl px-3 py-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 pr-3 border-r border-gray-200"
                      onClick={() => setIsDropdownOpen((prev) => !prev)}
                    >
                      <span className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center text-lg">
                        {selectedCountry.flag}
                      </span>
                      <span className="text-sm text-gray-800 font-semibold">
                        {selectedCountry.code}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <input
                      type="tel"
                      placeholder="905 965 5425"
                      className="w-full text-sm outline-none placeholder:text-black"
                    />
                  </div>

                  {isDropdownOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      <div className="px-3 py-2 border-b border-gray-200">
                        <input
                          type="text"
                          value={countryQuery}
                          onChange={(e) => setCountryQuery(e.target.value)}
                          placeholder="Search country"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                        />
                      </div>
                      <div className="max-h-60 overflow-auto">
                        {filteredCountryCodes.length ? (
                          filteredCountryCodes.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => {
                                setSelectedCountry(country);
                                setIsDropdownOpen(false);
                                setCountryQuery("");
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-sm text-gray-800"
                            >
                          <span className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center text-lg">
                            {country.flag}
                          </span>
                              <span className="font-semibold">{country.code}</span>
                              <span className="text-xs text-gray-500">
                                {country.label}
                              </span>
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-3 text-sm text-gray-500">No results found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-left">
                <label className="block text-xs text-gray-800 mb-1">
                  Additional Message
                </label>
                <textarea
                  rows="4"
                  placeholder="Placeholder"
                  className="w-full border border-gray-300 font-bold opacity-80 rounded px-3 py-2 text-sm outline-none placeholder:text-black placeholder:font-medium"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4 text-xs text-gray-700">
              <input 
                type="checkbox"
                className="
                  appearance-none           /* remove default browser style */
                  w-10 h-4                   /* size */
                  border-[1px]                  /* thickness */
                  border-[#61013B]         /* outline/border color */
                  rounded                  /* optional: rounded checkbox */
                  checked:bg-[#61013B]     /* fill color when checked */
                  checked:border-[#61013B] /* border stays same when checked */
                  cursor-pointer
                "
              />
              <p>
                By submitting this form, I agree to be contacted by PULO via email
                or phone regarding my inquiry. I also acknowledge that the
                information provided is subject to PULO’s Privacy Policy.
              </p>
            </div>

            <button
              type="submit"
              className="w-full bg-[#570033] text-white rounded-lg py-3 text-md font-semibold"
            >
              Submit
            </button>
          </form>
        </section>




      </main>

      <Footer />
    </div>
  );
}

export default Contact;
